// import the core library.
import ReactEChartsCore from "echarts-for-react/lib/core";
// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from "echarts/core";
// Import charts, all with Chart suffix
import {
  LineChart,
  // BarChart,
  // PieChart,
  // PieChart,
  // ScatterChart,
  // RadarChart,
  // MapChart,
  // TreeChart,
  // TreemapChart,
  // GraphChart,
  // GaugeChart,
  // FunnelChart,
  // ParallelChart,
  // SankeyChart,
  // BoxplotChart,
  // CandlestickChart,
  // EffectScatterChart,
  // LinesChart,
  // HeatmapChart,
  // PictorialBarChart,
  // ThemeRiverChart,
  // SunburstChart,
  // CustomChart,
} from "echarts/charts";
// import components, all suffixed with Component
import {
  // GridSimpleComponent,
  GridComponent,
  // PolarComponent,
  // RadarComponent,
  // GeoComponent,
  // SingleAxisComponent,
  // ParallelComponent,
  // CalendarComponent,
  // GraphicComponent,
  // ToolboxComponent,
  TooltipComponent,
  // AxisPointerComponent,
  // BrushComponent,
  TitleComponent,
  // TimelineComponent,
  // MarkPointComponent,
  // MarkLineComponent,
  // MarkAreaComponent,
  LegendComponent,
  // LegendScrollComponent,
  // LegendPlainComponent,
  DataZoomComponent,
  ToolboxComponent,
  BrushComponent,
} from "echarts/components";

import { UniversalTransition } from "echarts/features";

// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step
import {
  CanvasRenderer,
  // SVGRenderer,
} from "echarts/renderers";
import { useState, useCallback, useRef } from "react";
import { init } from "echarts";
import logger from "shared/utils/logging";

// Register the required components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LineChart,
  LegendComponent,
  ToolboxComponent,
  BrushComponent,
  CanvasRenderer,
  UniversalTransition,
]);

const EDynamicLinesChart = ({
  categories,
  series,
  title,
  legend,
  yAxis,
  onDataZoom,
  enableZoom = true,
  enableBrush = false,
  enableToolbox = true,
  initialZoomStart = 80,
  initialZoomEnd = 100,
  height = "100%",
  width = "100%",
  backgroundColor = "#fff",
  gridOptions = {},
  tooltipOptions = {},
  dataZoomOptions = {},
}) => {
  const [zoomStart, setZoomStart] = useState(initialZoomStart);
  const [zoomEnd, setZoomEnd] = useState(initialZoomEnd);
  const chartRef = useRef(null);

  // Reset zoom functionality
  const resetZoom = useCallback(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      chartInstance.dispatchAction({
        type: "dataZoom",
        start: initialZoomStart,
        end: initialZoomEnd,
      });
      setZoomStart(initialZoomStart);
      setZoomEnd(initialZoomEnd);
    }
  }, [initialZoomStart, initialZoomEnd]);

  // Zoom to specific range
  const zoomToRange = useCallback((start, end) => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      chartInstance.dispatchAction({
        type: "dataZoom",
        start: start,
        end: end,
      });
      setZoomStart(start);
      setZoomEnd(end);
    }
  }, []);

  // Zoom in by 10%
  const zoomIn = useCallback(() => {
    const range = zoomEnd - zoomStart;
    const newRange = Math.max(range * 0.8, 5); // Minimum 5% range
    const center = (zoomStart + zoomEnd) / 2;
    const newStart = Math.max(center - newRange / 2, 0);
    const newEnd = Math.min(center + newRange / 2, 100);
    zoomToRange(newStart, newEnd);
  }, [zoomStart, zoomEnd, zoomToRange]);

  // Zoom out by 10%
  const zoomOut = useCallback(() => {
    const range = zoomEnd - zoomStart;
    const newRange = Math.min(range * 1.2, 100);
    const center = (zoomStart + zoomEnd) / 2;
    const newStart = Math.max(center - newRange / 2, 0);
    const newEnd = Math.min(center + newRange / 2, 100);
    zoomToRange(newStart, newEnd);
  }, [zoomStart, zoomEnd, zoomToRange]);

  const option = {
    title: {
      text: title,
      subtext: "",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
      ...(typeof title === "object" ? title : {}),
    },

    tooltip: {
      trigger: "axis",
      position: function (pt) {
        return [pt[0], "10%"];
      },
      backgroundColor: "rgba(50,50,50,0.9)",
      borderColor: "#333",
      borderWidth: 1,
      textStyle: {
        color: "#fff",
      },
      axisPointer: {
        type: "cross",
        lineStyle: {
          color: "#999",
          width: 1,
          opacity: 0.8,
        },
      },
      ...tooltipOptions,
    },

    legend: {
      show: true,
      top: 30,
      ...legend,
    },

    grid: {
      left: "3%",
      right: "4%",
      bottom: enableZoom ? "15%" : "3%",
      top: "10%",
      containLabel: true,
      ...gridOptions,
    },

    toolbox: enableToolbox
      ? {
          show: true,
          feature: {
            myZoomIn: enableZoom
              ? {
                  show: true,
                  title: "Zoom In",
                  icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM17 13h-4v4h-2v-4H7v-2h4V7h2v4h4v2z",
                  onclick: () => zoomIn(),
                }
              : undefined,
            myZoomOut: enableZoom
              ? {
                  show: true,
                  title: "Zoom Out",
                  icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM17 13H7v-2h10v2z",
                  onclick: () => zoomOut(),
                }
              : undefined,
            // dataZoom: enableZoom
            //   ? {
            //       yAxisIndex: "none",
            //       title: {
            //         zoom: "Box Zoom",
            //         back: "Reset Zoom",
            //       },
            //     }
            //   : undefined,
            // brush: enableBrush
            //   ? {
            //       type: ["lineX", "clear"],
            //       title: {
            //         lineX: "Select Line",
            //         clear: "Clear Selection",
            //       },
            //     }
            //   : undefined,
            restore: {
              title: "Restore",
              onclick: () => resetZoom(),
            },
            saveAsImage: {
              title: "Save as Image",
              pixelRatio: 2,
            },
          },
          right: "2%",
          top: "5%",
          itemSize: 18,
          itemGap: 8,
          showTitle: true,
          iconStyle: {
            borderColor: "#666",
            color: "transparent",
          },
          emphasis: {
            iconStyle: {
              borderColor: "#1890ff",
              color: "#1890ff",
            },
          },
        }
      : undefined,

    // brush: enableBrush
    //   ? {
    //       toolbox: ["lineX", "clear"],
    //       xAxisIndex: "all",
    //     }
    //   : undefined,

    yAxis: {
      type: "value",
      boundaryGap: [0, "100%"],
      scale: true,
      splitLine: {
        lineStyle: {
          color: "#e0e0e0",
          type: "dashed",
        },
      },
      ...yAxis,
    },

    xAxis: {
      type: "time",
      boundaryGap: false,
      splitLine: {
        lineStyle: {
          color: "#e0e0e0",
          type: "dashed",
        },
      },
      axisLine: {
        lineStyle: {
          color: "#666",
        },
      },
    },

    dataZoom: enableZoom
      ? [
          {
            type: "inside",
            start: zoomStart,
            end: zoomEnd,
            zoomLock: false,
            throttle: 50,
            ...dataZoomOptions.inside,
          },
          {
            type: "slider",
            start: zoomStart,
            end: zoomEnd,
            show: true,
            height: 20,
            bottom: 10,
            handleSize: 8,
            textStyle: {
              color: "#666",
            },
            borderColor: "#ccc",
            fillerColor: "rgba(167,183,204,0.4)",
            handleStyle: {
              color: "#a7b7cc",
            },
            ...dataZoomOptions.slider,
          },
        ]
      : [],

    series:
      series?.map((serie) => ({
        ...serie,
        smooth: serie.smooth !== undefined ? serie.smooth : true,
        emphasis: {
          focus: "series",
          blurScope: "coordinateSystem",
        },
        lineStyle: {
          width: serie.lineStyle?.width || 2,
          ...serie.lineStyle,
        },
        symbolSize: serie.symbolSize || 4,
        animation: true,
        animationDuration: 1000,
        // animationEasing: "cubicInOut",
      })) || [],
  };

  const onChartReadyCallback = useCallback((chart) => {
    logger.log("Chart is ready");
    chartRef.current = { getEchartsInstance: () => chart };
  }, []);

  // Event handler for dataZoom
  const onDataZoomEvent = useCallback(
    (params) => {
      logger.log("DataZoom event:", params);

      let start, end;
      if (params.batch && params.batch.length > 0) {
        // When multiple dataZoom components exist, batch contains the data
        const dataZoomInfo = params.batch[0];
        start = dataZoomInfo.start;
        end = dataZoomInfo.end;
      } else {
        // Direct params (less common case)
        start = params.start || params.startValue;
        end = params.end || params.endValue;
      }

      // logger.log(`DataZoom range: ${start}% - ${end}%`);

      // Clamp start and end to [0, 100]
      const clampedStart = Math.max(0, Math.min(100, start));
      const clampedEnd = Math.max(0, Math.min(100, end));

      // Update the zoom state
      setZoomStart(clampedStart);
      setZoomEnd(clampedEnd);

      // Call the callback if provided
      if (onDataZoom) {
        onDataZoom({ start: clampedStart, end: clampedEnd, params });
      }
    },
    [onDataZoom]
  );

  // Event handler for brush selection
  const onBrushEvent = useCallback((params) => {
    logger.log("Brush event:", params);
    if (params.areas && params.areas.length > 0) {
      const area = params.areas[0];
      if (area.coordRange) {
        const [startTime, endTime] = area.coordRange;
        logger.log(`Brush selection: ${startTime} - ${endTime}`);
      }
    }
  }, []);

  // Event handler for restore (reset)
  const onRestoreEvent = useCallback(() => {
    logger.log("Chart restored");
    resetZoom();
  }, [resetZoom]);

  // Define events dictionary
  const EventsDict = {
    datazoom: onDataZoomEvent,
    brush: onBrushEvent,
    restore: onRestoreEvent,
  };

  return (
    <div style={{ height, width, position: "relative" }}>
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={option}
        notMerge={true}
        lazyUpdate={true}
        theme={"theme_name"}
        onChartReady={onChartReadyCallback}
        onEvents={EventsDict}
        style={{ height: "100%", width: "100%", background: backgroundColor }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};

export default EDynamicLinesChart;
